import React, { useEffect, useState, useCallback } from 'react';
import { API, showError } from '../helpers';
import {
  Card,
  Grid,
  Header,
  Icon,
  Loader,
  Message,
  Segment,
  Statistic,
  Table,
  Label,
  Button,
  Dropdown,
  Popup,
  Progress,
  Divider,
} from 'semantic-ui-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from 'recharts';
import './GitMonitorDashboard.css';

const CHART_COLORS = {
  primary: '#6366f1',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  purple: '#8b5cf6',
  pink: '#ec4899',
  cyan: '#06b6d4',
};

const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const GitMonitorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topRepos, setTopRepos] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [operations, setOperations] = useState([]);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [timeRange, setTimeRange] = useState(24);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const res = await API.get('/api/admin/git/monitor/overview');
      if (res.data.success) {
        setOverview(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load overview:', error);
    }
  }, []);

  const loadTrend = useCallback(async () => {
    try {
      const res = await API.get(`/api/admin/git/monitor/trend?hours=${timeRange}`);
      if (res.data.success) {
        setTrend(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load trend:', error);
    }
  }, [timeRange]);

  const loadTopRepos = useCallback(async () => {
    try {
      const res = await API.get('/api/admin/git/monitor/top-repos?days=7&limit=10');
      if (res.data.success) {
        setTopRepos(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load top repos:', error);
    }
  }, []);

  const loadTopUsers = useCallback(async () => {
    try {
      const res = await API.get('/api/admin/git/monitor/top-users?days=7&limit=10');
      if (res.data.success) {
        setTopUsers(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load top users:', error);
    }
  }, []);

  const loadOperations = useCallback(async () => {
    try {
      const res = await API.get('/api/admin/git/monitor/operations?page=1&page_size=15');
      if (res.data.success) {
        setOperations(res.data.data?.logs || []);
      }
    } catch (error) {
      console.error('Failed to load operations:', error);
    }
  }, []);

  const loadServiceStatus = useCallback(async () => {
    try {
      const res = await API.get('/api/admin/git/monitor/status');
      if (res.data.success) {
        setServiceStatus(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load service status:', error);
    }
  }, []);

  const loadAllData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    await Promise.all([
      loadOverview(),
      loadTrend(),
      loadTopRepos(),
      loadTopUsers(),
      loadOperations(),
      loadServiceStatus(),
    ]);
    
    setLastUpdated(new Date());
    if (showRefresh) setRefreshing(false);
    else setLoading(false);
  }, [loadOverview, loadTrend, loadTopRepos, loadTopUsers, loadOperations, loadServiceStatus]);

  useEffect(() => {
    loadAllData();
    const interval = setInterval(() => loadAllData(true), 30000);
    return () => clearInterval(interval);
  }, [loadAllData]);

  useEffect(() => {
    loadTrend();
  }, [timeRange, loadTrend]);

  const formatDuration = (ms) => {
    if (!ms || ms === 0) return '0ms';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // 服务状态卡片
  const renderServiceStatus = () => {
    if (!serviceStatus) return null;

    const services = [
      { key: 'ssh', name: 'SSH Server', icon: 'terminal', data: serviceStatus.ssh, color: '#6366f1' },
      { key: 'http', name: 'HTTP Server', icon: 'globe', data: serviceStatus.http, color: '#10b981' },
      { key: 'git', name: 'Git Daemon', icon: 'code branch', data: serviceStatus.git, color: '#f59e0b' },
      { key: 'stats', name: 'Stats Server', icon: 'chart line', data: serviceStatus.stats, color: '#8b5cf6' },
    ];

    return (
      <div className="service-status-grid">
        {services.map((service) => {
          const isRunning = service.data?.status === 'running';
          return (
            <div key={service.key} className={`service-card ${isRunning ? 'running' : 'stopped'}`}>
              <div className="service-icon" style={{ background: `linear-gradient(135deg, ${service.color}20, ${service.color}40)` }}>
                <Icon name={service.icon} style={{ color: service.color }} />
              </div>
              <div className="service-info">
                <div className="service-name">{service.name}</div>
                <div className="service-port">:{service.data?.port || '-'}</div>
              </div>
              <div className={`service-status ${isRunning ? 'online' : 'offline'}`}>
                <span className="status-dot"></span>
                {isRunning ? '运行中' : '已停止'}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 核心指标卡片
  const renderMetricCards = () => {
    if (!overview) return null;

    const lastHour = overview.last_hour || {};
    const today = overview.today || {};
    const last24 = overview.last_24_hours || {};
    const last7d = overview.last_7_days || {};

    const metrics = [
      {
        title: '最近1小时请求',
        value: formatNumber(lastHour.total_requests || 0),
        subtitle: `成功率 ${(lastHour.success_rate || 100).toFixed(1)}%`,
        icon: 'clock outline',
        color: '#6366f1',
        trend: lastHour.total_requests > 0 ? 'up' : 'neutral',
      },
      {
        title: '今日请求总量',
        value: formatNumber(today.total_requests || 0),
        subtitle: `${today.unique_users || 0} 位用户`,
        icon: 'calendar check outline',
        color: '#10b981',
        trend: 'up',
      },
      {
        title: '平均响应时间',
        value: formatDuration(lastHour.avg_duration || 0),
        subtitle: '最近1小时',
        icon: 'stopwatch',
        color: '#f59e0b',
        trend: (lastHour.avg_duration || 0) < 1000 ? 'good' : 'warning',
      },
      {
        title: '近7天成功率',
        value: `${(last7d.success_rate || 100).toFixed(1)}%`,
        subtitle: `${formatNumber(last7d.total_requests || 0)} 次请求`,
        icon: 'check circle outline',
        color: '#8b5cf6',
        trend: (last7d.success_rate || 100) > 95 ? 'good' : 'warning',
      },
    ];

    return (
      <div className="metrics-grid">
        {metrics.map((metric, index) => (
          <div key={index} className="metric-card">
            <div className="metric-header">
              <div className="metric-icon" style={{ background: `${metric.color}15`, color: metric.color }}>
                <Icon name={metric.icon} />
              </div>
              <div className="metric-title">{metric.title}</div>
            </div>
            <div className="metric-value" style={{ color: metric.color }}>{metric.value}</div>
            <div className="metric-subtitle">{metric.subtitle}</div>
          </div>
        ))}
      </div>
    );
  };

  // 今日详细统计
  const renderTodayStats = () => {
    if (!overview) return null;
    const today = overview.today || {};

    return (
      <div className="today-stats">
        <div className="stat-item">
          <div className="stat-label">活跃仓库</div>
          <div className="stat-value">{today.unique_repos || 0}</div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <div className="stat-label">成功请求</div>
          <div className="stat-value success">{today.success_count || 0}</div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <div className="stat-label">失败请求</div>
          <div className="stat-value danger">{today.failed_count || 0}</div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <div className="stat-label">入站流量</div>
          <div className="stat-value">{formatBytes(overview.last_hour?.total_bytes_in || 0)}</div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <div className="stat-label">出站流量</div>
          <div className="stat-value">{formatBytes(overview.last_hour?.total_bytes_out || 0)}</div>
        </div>
      </div>
    );
  };

  // 操作分布饼图
  const renderDistributionCharts = () => {
    const opData = overview?.operation_distribution 
      ? Object.entries(overview.operation_distribution).map(([name, value]) => ({
          name: name === 'clone' ? '克隆' : name === 'push' ? '推送' : name === 'fetch' ? '拉取' : name === 'push_refs' ? '推送引用' : name,
          value,
        }))
      : [];

    const protocolData = overview?.protocol_distribution
      ? Object.entries(overview.protocol_distribution).map(([name, value]) => ({
          name: name.toUpperCase(),
          value,
        }))
      : [];

    const renderPie = (data, title) => {
      if (data.length === 0) {
        return (
          <div className="empty-chart">
            <Icon name="pie chart" size="huge" />
            <p>暂无{title}数据</p>
          </div>
        );
      }

      return (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value) => [value, '次数']}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
            />
            <Legend 
              verticalAlign="bottom"
              height={36}
              formatter={(value) => <span style={{ color: '#64748b', fontSize: '12px' }}>{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      );
    };

    return (
      <Grid columns={2} stackable>
        <Grid.Column>
          <div className="chart-card">
            <div className="chart-header">
              <Icon name="tasks" />
              <span>操作类型分布</span>
              <Label size="tiny" color="blue">今日</Label>
            </div>
            {renderPie(opData, '操作')}
          </div>
        </Grid.Column>
        <Grid.Column>
          <div className="chart-card">
            <div className="chart-header">
              <Icon name="sitemap" />
              <span>协议分布</span>
              <Label size="tiny" color="blue">今日</Label>
            </div>
            {renderPie(protocolData, '协议')}
          </div>
        </Grid.Column>
      </Grid>
    );
  };

  // 趋势图
  const renderTrendChart = () => {
    if (!trend || trend.length === 0) {
      return (
        <div className="empty-chart large">
          <Icon name="line graph" size="huge" />
          <p>暂无趋势数据</p>
          <p className="hint">Git 操作记录后将自动显示趋势</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={320}>
        <AreaChart data={trend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis 
            dataKey="hour" 
            tickFormatter={(value) => value ? value.split(' ')[1] || value : ''}
            stroke="#94a3b8"
            fontSize={11}
          />
          <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} />
          <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} domain={[0, 100]} />
          <Tooltip 
            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
            formatter={(value, name) => {
              if (name === '成功率') return [`${value.toFixed(1)}%`, name];
              return [value, name];
            }}
          />
          <Legend />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="total_requests"
            stroke={CHART_COLORS.primary}
            fillOpacity={1}
            fill="url(#colorRequests)"
            name="请求数"
            strokeWidth={2}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="success_rate"
            stroke={CHART_COLORS.success}
            name="成功率"
            strokeWidth={2}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  // 热门仓库排行
  const renderTopReposChart = () => {
    if (!topRepos || topRepos.length === 0) {
      return (
        <div className="empty-chart">
          <Icon name="folder outline" size="huge" />
          <p>暂无仓库数据</p>
        </div>
      );
    }

    const data = topRepos.slice(0, 8).map(repo => ({
      ...repo,
      shortName: repo.repo_name?.split('/').pop() || repo.repo_name,
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis type="number" stroke="#94a3b8" fontSize={11} />
          <YAxis 
            dataKey="shortName" 
            type="category" 
            width={100}
            stroke="#94a3b8"
            fontSize={11}
            tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
          />
          <Tooltip 
            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
          />
          <Legend />
          <Bar dataKey="clone_count" fill={CHART_COLORS.primary} name="克隆" radius={[0, 4, 4, 0]} />
          <Bar dataKey="push_count" fill={CHART_COLORS.success} name="推送" radius={[0, 4, 4, 0]} />
          <Bar dataKey="pull_count" fill={CHART_COLORS.warning} name="拉取" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // 活跃用户排行
  const renderTopUsersTable = () => {
    if (!topUsers || topUsers.length === 0) {
      return (
        <div className="empty-chart">
          <Icon name="users" size="huge" />
          <p>暂无用户数据</p>
        </div>
      );
    }

    const maxRequests = Math.max(...topUsers.map(u => u.total_requests || 0));

    return (
      <div className="users-ranking">
        {topUsers.slice(0, 8).map((user, index) => (
          <div key={index} className="user-rank-item">
            <div className={`rank-badge rank-${index + 1}`}>
              {index < 3 ? <Icon name={['trophy', 'medal', 'certificate'][index]} /> : index + 1}
            </div>
            <div className="user-info">
              <div className="user-name">{user.username || `User #${user.user_id}`}</div>
              <div className="user-stats">
                <span><Icon name="upload" size="small" />{user.push_count}</span>
                <span><Icon name="download" size="small" />{user.clone_count}</span>
                <span><Icon name="database" size="small" />{formatBytes((user.total_bytes_in || 0) + (user.total_bytes_out || 0))}</span>
              </div>
            </div>
            <div className="user-progress">
              <div className="progress-bar" style={{ width: `${(user.total_requests / maxRequests) * 100}%` }}></div>
              <span className="request-count">{user.total_requests}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 最近操作日志
  const renderOperationsTable = () => {
    if (!operations || operations.length === 0) {
      return (
        <div className="empty-chart large">
          <Icon name="history" size="huge" />
          <p>暂无操作日志</p>
          <p className="hint">Git 操作（clone/push/fetch）将自动记录到这里</p>
        </div>
      );
    }

    const getOperationConfig = (op) => {
      const configs = {
        clone: { icon: 'download', color: '#6366f1', label: '克隆' },
        push: { icon: 'upload', color: '#10b981', label: '推送' },
        fetch: { icon: 'sync', color: '#f59e0b', label: '拉取' },
        pull: { icon: 'sync', color: '#f59e0b', label: '拉取' },
        push_refs: { icon: 'code branch', color: '#8b5cf6', label: '推送引用' },
      };
      return configs[op] || { icon: 'code branch', color: '#64748b', label: op };
    };

    return (
      <div className="operations-list">
        {operations.map((op, index) => {
          const config = getOperationConfig(op.operation);
          const isSuccess = op.status === 'success';
          return (
            <div key={index} className={`operation-item ${isSuccess ? '' : 'failed'}`}>
              <div className="op-icon" style={{ background: `${config.color}15`, color: config.color }}>
                <Icon name={config.icon} />
              </div>
              <div className="op-info">
                <div className="op-main">
                  <span className="op-label">{config.label}</span>
                  <span className="op-repo">
                    {op.repo_name?.length > 30 ? '...' + op.repo_name.slice(-27) : op.repo_name}
                  </span>
                </div>
                <div className="op-meta">
                  <span><Icon name="user" size="small" />{op.git_username || op.user_id || '匿名'}</span>
                  <span><Icon name="server" size="small" />{op.protocol?.toUpperCase()}</span>
                  <span><Icon name="clock" size="small" />{formatDuration(op.duration_ms)}</span>
                </div>
              </div>
              <div className="op-status">
                <div className={`status-badge ${isSuccess ? 'success' : 'failed'}`}>
                  <Icon name={isSuccess ? 'check' : 'times'} />
                  {isSuccess ? '成功' : '失败'}
                </div>
                <div className="op-time">
                  {new Date(op.created_at).toLocaleString('zh-CN', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="git-monitor-loading">
        <Loader active size="large">
          <p>加载监控数据中...</p>
        </Loader>
      </div>
    );
  }

  return (
    <div className="git-monitor-dashboard">
      {/* 页面头部 */}
      <div className="dashboard-header">
        <div className="header-left">
          <h2><Icon name="dashboard" /> Git 监控中心</h2>
          <p>实时监控 Git Server 运行状态和操作统计</p>
        </div>
        <div className="header-right">
          <span className="last-updated">
            <Icon name="clock outline" />
            {lastUpdated ? `更新于 ${lastUpdated.toLocaleTimeString('zh-CN')}` : '加载中...'}
          </span>
          <Button
            icon
            size="small"
            loading={refreshing}
            onClick={() => loadAllData(true)}
            className="refresh-btn"
          >
            <Icon name="refresh" />
          </Button>
        </div>
      </div>

      {/* 服务状态 */}
      <div className="section">
        <div className="section-header">
          <Icon name="server" />
          <span>服务状态</span>
        </div>
        {renderServiceStatus()}
      </div>

      {/* 核心指标 */}
      <div className="section">
        <div className="section-header">
          <Icon name="chart bar" />
          <span>核心指标</span>
        </div>
        {renderMetricCards()}
        {renderTodayStats()}
      </div>

      {/* 分布图 */}
      <div className="section">
        {renderDistributionCharts()}
      </div>

      {/* 趋势图 */}
      <div className="section">
        <div className="chart-card full-width">
          <div className="chart-header">
            <div>
              <Icon name="line graph" />
              <span>请求趋势</span>
            </div>
            <Dropdown
              selection
              compact
              options={[
                { key: 24, text: '最近24小时', value: 24 },
                { key: 48, text: '最近48小时', value: 48 },
                { key: 168, text: '最近7天', value: 168 },
              ]}
              value={timeRange}
              onChange={(e, { value }) => setTimeRange(value)}
            />
          </div>
          {renderTrendChart()}
        </div>
      </div>

      {/* 排行榜 */}
      <Grid columns={2} stackable>
        <Grid.Column>
          <div className="section">
            <div className="chart-card">
              <div className="chart-header">
                <Icon name="trophy" />
                <span>热门仓库</span>
                <Label size="tiny" color="orange">近7天</Label>
              </div>
              {renderTopReposChart()}
            </div>
          </div>
        </Grid.Column>
        <Grid.Column>
          <div className="section">
            <div className="chart-card">
              <div className="chart-header">
                <Icon name="users" />
                <span>活跃用户排行</span>
                <Label size="tiny" color="orange">近7天</Label>
              </div>
              {renderTopUsersTable()}
            </div>
          </div>
        </Grid.Column>
      </Grid>

      {/* 最近操作 */}
      <div className="section">
        <div className="chart-card full-width">
          <div className="chart-header">
            <Icon name="history" />
            <span>最近操作日志</span>
            <Label size="tiny">实时</Label>
          </div>
          {renderOperationsTable()}
        </div>
      </div>
    </div>
  );
};

export default GitMonitorDashboard;
